import {deleteCookie, getCookie, isValidCookieValue, setCookie} from '@github-ui/cookies'
import {postTask} from '@github-ui/scheduler'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

export type Listener = () => void

export interface CookieWriteOptions {
  /** Cookie lifetime in days. Defaults to 365. */
  expiresInDays?: number
  /** SameSite attribute. Defaults to 'lax'. */
  sameSite?: 'lax' | 'strict' | 'none'
}

const DEFAULT_EXPIRES_IN_DAYS = 365

/**
 * Cookie-backed store with an in-memory cache + listener registry so components
 * sharing a key stay in sync. Reads are SSR-safe: `getCookie` returns undefined
 * with no `document`, so `read` falls back to the seed.
 */
export const createCookieStore = () => {
  const cache = new Map<string, unknown>()
  const listeners = new Map<string, Set<Listener>>()

  // Deferred persistence: the actual `document.cookie` write is batched and
  // flushed via postTask so it doesn't block the interaction (INP) handler.
  const pendingWrites = new Map<string, {serialized: string; options?: CookieWriteOptions}>()
  let flushScheduled = false

  function serialize<T>(value: T): string {
    return encodeURIComponent(JSON.stringify(value))
  }

  function deserialize<T>(raw: string, fallback: T): T {
    try {
      return JSON.parse(decodeURIComponent(raw)) as T
    } catch {
      return fallback
    }
  }

  function isEqual<T>(a: T, b: T): boolean {
    if (a === b) return true
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return false
    }
  }

  function emit(key: string): void {
    const set = listeners.get(key)
    if (set) {
      for (const listener of set) {
        listener()
      }
    }
  }

  function subscribe(key: string, listener: Listener): () => void {
    let set = listeners.get(key)
    if (!set) {
      set = new Set()
      listeners.set(key, set)
    }
    set.add(listener)
    return () => {
      set.delete(listener)
      if (set.size === 0) {
        listeners.delete(key)
      }
    }
  }

  // Cached value wins (required for a stable useSyncExternalStore snapshot).
  function read<T>(key: string, initial: T): T {
    if (cache.has(key)) {
      return cache.get(key) as T
    }
    const cookie = getCookie(key)
    const value = cookie ? deserialize(cookie.value, initial) : initial
    cache.set(key, value)
    return value
  }

  function write<T>(key: string, value: T, options?: CookieWriteOptions): void {
    const prev = cache.get(key)
    if (cache.has(key) && isEqual(prev, value)) return

    const serialized = serialize(value)
    if (!isValidCookieValue(serialized)) {
      // Don't update cache/subscribers for a value we can't persist, or React
      // state would silently diverge from what survives a hard navigation.
      // eslint-disable-next-line no-console
      console.warn(`useCookieStorage: value for "${key}" could not be persisted to a cookie and was not applied.`)
      return
    }

    cache.set(key, value)
    emit(key)

    if (ssrSafeWindow?.__SAFE_STORAGE_FORCE_SYNC_WRITES__) {
      pendingWrites.delete(key)
      persist(key, serialized, options)
    } else {
      pendingWrites.set(key, {serialized, options})
      scheduleFlush()
    }
  }

  function persist(key: string, serialized: string, options?: CookieWriteOptions): void {
    const expiresInDays = options?.expiresInDays ?? DEFAULT_EXPIRES_IN_DAYS
    const expires = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toUTCString()
    setCookie(key, serialized, expires, false, options?.sameSite ?? 'lax')
  }

  function flushToCookies(): void {
    try {
      for (const [k, pending] of pendingWrites) {
        try {
          persist(k, pending.serialized, pending.options)
        } catch {
          // One key failing to persist shouldn't abort the rest of the batch.
        }
      }
      pendingWrites.clear()
    } finally {
      flushScheduled = false
    }
  }

  function scheduleFlush(): void {
    if (!flushScheduled) {
      flushScheduled = true
      postTask(flushToCookies, {priority: 'background'})
    }
  }

  /**
   * Reset a key to `defaultValue` and delete its cookie, so the next load falls
   * back to the seed. Mirrors `resetStorage` in the local/session store.
   */
  function reset<T>(key: string, defaultValue: T): void {
    const prev = cache.get(key)
    if (cache.has(key) && isEqual(prev, defaultValue)) return

    pendingWrites.delete(key)
    cache.set(key, defaultValue)
    emit(key)
    deleteCookie(key)
  }

  /** Test helper; does not delete the underlying cookies. */
  function clearCache(): void {
    pendingWrites.clear()
    const keys = [...cache.keys()]
    cache.clear()
    for (const key of keys) {
      emit(key)
    }
  }

  // Flush deferred writes when the page is backgrounded so they aren't lost on
  // tab close or navigation.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushToCookies()
      }
    })
  }

  return {cache, subscribe, read, write, reset, clearCache}
}

export const cookieStore = createCookieStore()

declare global {
  interface Window {
    __SAFE_STORAGE_CLEAR_ALL_CACHES__?: () => void
    __SAFE_STORAGE_FORCE_SYNC_WRITES__?: boolean
  }
}

// Chain onto the shared test-cleanup global so bootstrap clears cookie caches
// alongside local/session storage, regardless of module load order.
if (typeof window !== 'undefined') {
  const previousClear = window.__SAFE_STORAGE_CLEAR_ALL_CACHES__
  window.__SAFE_STORAGE_CLEAR_ALL_CACHES__ = () => {
    previousClear?.()
    cookieStore.clearCache()
  }
}
